import { Controller } from "@hotwired/stimulus"

// Public sub portal + compliance upload pages: presign → S3 PUT on file pick,
// then stash the cache name into the form's hidden inputs so the plain form
// POST carries the reference.
export default class extends Controller {
  static targets = ["lumpWrap", "scheduleWrap", "linesBody", "linesTotal", "payAppModal", "coModal",
                    "approvalModal", "approverRows", "bidDropZone", "bidFileInput", "bidReadStatus",
                    "withdrawModal", "feeForm", "feeSaveStatus", "modeSwitchModal",
                    "docsNeededModal", "feeEmptyModal", "docsSection", "docExceptionModal", "exceptionReason", "exceptionError",
                    "docsInviteModal", "docsInviteName", "docsInviteEmail", "docsInviteError", "docsInviteArea", "docsInviteSubmit",
                    "signModal", "signRows", "signRowTemplate", "signForm", "signCert", "signCertAgree",
                    "appResendBar", "coResendBar", "resendModal", "resendRows",
                    "resendRowTemplate", "resendError", "resendSubmit",
                    "invLinesBody", "invLinesTotal", "invModal", "invResendBar", "stepsPanel",
                    "signWaiverModal"]
  static values = { presignUrl: String, readUrl: String }

  // ── Pay application modal (Phelipe 2026-07-23): G703-style per-line
  // billing — this-period amounts per fee schedule line + approved COs. ──
  openPayApp() { this._resetPayApp(); this.payAppModalTarget.classList.remove("hidden") }
  closePayApp() { this.payAppModalTarget.classList.add("hidden") }
  openCo() { this._resetCo(); this.coModalTarget.classList.remove("hidden") }
  closeCo() { this.coModalTarget.classList.add("hidden") }
  stopPay(e) { e.stopPropagation() }

  // Click a pay application row: view it, and edit while still in review
  // (Phelipe 2026-07-23). The same modal serves new / edit / read-only.
  openPayAppRow(e) {
    const d = e.currentTarget.dataset
    this._resetPayApp()
    const modal = this.payAppModalTarget
    const form = modal.querySelector("form")
    const editable = d.editable === "true"
    form.action = d.updateUrl
    // AI drop zone only on editable drafts (Phelipe 2026-07-28) — hidden
    // once it's pending signature or decided.
    modal.querySelector("[data-ai-zone]")?.classList.toggle("hidden", !(editable && d.signable === "true"))
    let lines = {}
    try { lines = JSON.parse(d.lines || "{}") } catch { /* view still works */ }
    const num = modal.querySelector("[data-app-number]")
    if (num) { num.value = d.number || ""; num.disabled = !editable }
    const per = modal.querySelector("[data-app-period]")
    if (per) { per.value = d.periodEnd || ""; per.disabled = !editable }
    modal.querySelectorAll("[data-app-row]").forEach((row) => {
      const input = row.querySelector('[name="app_lines[][amount]"]')
      if (!input) return
      const v = lines[row.dataset.lineKey]
      input.value = v ? this._fmtMoney(v) : ""
      input.disabled = !editable
      // Qty / % companions ride the same editable state (Phelipe 2026-07-28);
      // qty stays locked on rows without a unit price.
      const qty = row.querySelector("[data-app-qty]")
      if (qty && !qty.hasAttribute("data-qty-locked")) qty.disabled = !editable
      const pct = row.querySelector("[data-app-pct]")
      if (pct) pct.disabled = !editable
    })
    const waiver = modal.querySelector("[data-app-waiver]")
    const check = waiver?.querySelector('input[type="checkbox"]')
    if (waiver) waiver.classList.add("hidden")
    if (check) { check.required = false; check.checked = true }
    const submit = modal.querySelector("[data-app-submit]")
    if (submit) { submit.textContent = "Save changes"; submit.classList.toggle("hidden", !editable) }
    const title = modal.querySelector("[data-pay-app-title]")
    if (title) title.textContent = editable ? `Edit ${d.display || "pay application"}` : (d.display || "Pay application")
    // History Log (Larry 2026-07-23) — clone this invoice's server-rendered
    // log (same partial the GC finance pages use) into the modal.
    const hist = modal.querySelector("[data-pay-app-history]")
    if (hist) {
      hist.innerHTML = ""
      const tpl = document.querySelector(`template[data-pay-app-history-for="${d.invoiceId}"]`)
      if (tpl) hist.appendChild(tpl.content.cloneNode(true))
      hist.classList.toggle("hidden", !tpl)
    }
    // Sign & send (Phelipe 2026-07-27) — only while it's still in review
    // and no chain is already out.
    const signBtn = modal.querySelector("[data-app-sign]")
    const signable = d.signable === "true"
    if (signBtn) signBtn.classList.toggle("hidden", !signable)
    this._signCtx = signable ? { url: d.signUrl, label: d.signLabel } : null
    this._setResend(this.hasAppResendBarTarget && this.appResendBarTarget, d)
    this._setPdfLink(modal.querySelector("[data-app-pdf]"), d.pdfUrl)
    this.payAppInput()
    modal.classList.remove("hidden")
  }

  // Export PDF link in the doc pop-ups (Phelipe 2026-07-27) — only when an
  // existing document was opened (new/unsaved ones have nothing to export).
  _setPdfLink(link, url) {
    if (!link) return
    if (url) { link.href = url; link.classList.remove("hidden") } else { link.classList.add("hidden") }
  }

  // Export on a NEW pay application (Phelipe 2026-07-30): no saved copy yet,
  // so build a preview from the typed amounts. Saved apps keep their href
  // (the server copy, signatures included) and skip this handler.
  exportPayAppPdf(e) {
    const link = e.currentTarget
    if (link.getAttribute("href")) return

    e.preventDefault()
    const modal = this.payAppModalTarget
    const qs = new URLSearchParams()
    const num = modal.querySelector("[data-app-number]")?.value?.trim()
    if (num) qs.append("number", num)
    const per = modal.querySelector("[data-app-period]")?.value
    if (per) qs.append("period_end", per)
    modal.querySelectorAll("[data-app-row]").forEach((row) => {
      const amount = this._num(row.querySelector('[name="app_lines[][amount]"]')?.value)
      if (!amount) return
      qs.append("app_lines[][sub_contract_line_id]", row.querySelector('[name="app_lines[][sub_contract_line_id]"]')?.value || "")
      qs.append("app_lines[][sub_contract_change_id]", row.querySelector('[name="app_lines[][sub_contract_change_id]"]')?.value || "")
      qs.append("app_lines[][description]", row.querySelector('[name="app_lines[][description]"]')?.value || "")
      qs.append("app_lines[][amount]", String(amount))
    })
    window.open(`${link.dataset.previewUrl}?${qs.toString()}`, "_blank")
  }

  // Show the "waiting on X · resend" bar when the row carries a pending
  // signer; hide it otherwise (Phelipe 2026-07-27). The button opens the
  // confirm pop-up, which posts via fetch and toasts — no page reload.
  _setResend(bar, d) {
    if (!bar) return
    const pending = (d.pendingName || "").trim()
    if (pending && d.resendUrl) {
      this._resendCtx = { url: d.resendUrl, name: pending, email: (d.pendingEmail || "").trim() }
      const name = bar.querySelector("[data-resend-name]")
      if (name) name.textContent = pending
      bar.classList.remove("hidden")
    } else {
      this._resendCtx = null
      bar.classList.add("hidden")
    }
  }

  openResendModal() {
    if (!this._resendCtx || !this.hasResendModalTarget) return
    const modal = this.resendModalTarget
    const name = modal.querySelector("[data-resend-to-name]")
    if (name) name.textContent = this._resendCtx.name
    const email = modal.querySelector("[data-resend-to-email]")
    if (email) email.textContent = this._resendCtx.email ? `(${this._resendCtx.email})` : ""
    if (this.hasResendRowsTarget) this.resendRowsTarget.innerHTML = ""
    if (this.hasResendErrorTarget) this.resendErrorTarget.classList.add("hidden")
    modal.classList.remove("hidden")
  }

  closeResendModal() { if (this.hasResendModalTarget) this.resendModalTarget.classList.add("hidden") }

  addResendRow() {
    if (this.hasResendRowsTarget && this.hasResendRowTemplateTarget) {
      this.resendRowsTarget.appendChild(this.resendRowTemplateTarget.content.cloneNode(true))
    }
  }

  removeResendRow(e) { e.currentTarget.closest("[data-resend-row]")?.remove() }

  async submitResend() {
    if (!this._resendCtx) return
    const signers = Array.from(this.resendRowsTarget?.querySelectorAll("[data-resend-row]") || [])
      .map((row) => ({
        name: row.querySelector("[data-rname]")?.value?.trim() || "",
        email: row.querySelector("[data-remail]")?.value?.trim() || ""
      }))
      .filter((s) => s.email || s.name)
    const btn = this.hasResendSubmitTarget ? this.resendSubmitTarget : null
    if (btn) btn.disabled = true
    try {
      const resp = await fetch(this._resendCtx.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ signers })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not resend the request.")
      this.closeResendModal()
      this._showToast(data.message || "Signature request resent.")
    } catch (err) {
      if (this.hasResendErrorTarget) {
        this.resendErrorTarget.textContent = err.message
        this.resendErrorTarget.classList.remove("hidden")
      }
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // Standard top-right toast (same pattern as fee_schedule_controller).
  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  _resetPayApp() {
    const modal = this.payAppModalTarget
    const form = modal.querySelector("form")
    if (form?.dataset.createUrl) form.action = form.dataset.createUrl
    modal.querySelector("[data-ai-zone]")?.classList.remove("hidden")
    const num = modal.querySelector("[data-app-number]")
    if (num) { num.value = ""; num.disabled = false }
    const per = modal.querySelector("[data-app-period]")
    if (per) { per.value = ""; per.disabled = false }
    modal.querySelectorAll("[data-app-row]").forEach((row) => {
      const input = row.querySelector('[name="app_lines[][amount]"]')
      if (input) { input.value = ""; input.disabled = false }
      const qty = row.querySelector("[data-app-qty]")
      if (qty) { qty.value = ""; qty.disabled = qty.hasAttribute("data-qty-locked") }
      const pct = row.querySelector("[data-app-pct]")
      if (pct) { pct.value = ""; pct.disabled = false }
    })
    const waiver = modal.querySelector("[data-app-waiver]")
    const check = waiver?.querySelector('input[type="checkbox"]')
    if (waiver) waiver.classList.remove("hidden")
    if (check) { check.required = true; check.checked = false }
    const submit = modal.querySelector("[data-app-submit]")
    if (submit) { submit.textContent = "Submit pay application"; submit.classList.remove("hidden") }
    const title = modal.querySelector("[data-pay-app-title]")
    if (title) title.textContent = "New pay application"
    const hist = modal.querySelector("[data-pay-app-history]")
    if (hist) { hist.innerHTML = ""; hist.classList.add("hidden") }
    const signBtn = modal.querySelector("[data-app-sign]")
    if (signBtn) signBtn.classList.add("hidden")
    if (this.hasAppResendBarTarget) this.appResendBarTarget.classList.add("hidden")
    // New application: keep Export visible in preview mode (no href — the
    // click handler builds the preview from the typed amounts).
    const pdf = modal.querySelector("[data-app-pdf]")
    if (pdf) { pdf.removeAttribute("href"); pdf.classList.remove("hidden") }
    this._signCtx = null
    this.payAppInput()
  }

  // ── Fee schedule approval request (Phelipe 2026-07-23) ─────────────────
  // The button always renders while editable (item 21, 2026-07-28), so an
  // empty schedule gets a nudge pop-up first — reading the live grid, no
  // reload needed after autosave. Then the docs gate as before; the server's
  // zero-amount + docs guards stay the backstop.
  openApproval() {
    if (this._feeScheduleTotal() <= 0 && this.hasFeeEmptyModalTarget) {
      this.feeEmptyModalTarget.classList.remove("hidden")
      return
    }
    const needed = [...this.element.querySelectorAll("[data-doc-row]")]
      .filter((r) => ["missing", "expired"].includes(r.dataset.docStatus))
    if (needed.length && this.hasDocsNeededModalTarget) {
      const modal = this.docsNeededModalTarget
      const list = modal.querySelector("[data-docs-needed-list]")
      if (list) list.textContent = needed.map((r) => r.querySelector("span")?.textContent?.trim()).filter(Boolean).join(" · ")
      // Fee context: nothing typed to lose — plain "Take me there".
      modal.querySelector("[data-docs-goto]")?.classList.remove("hidden")
      modal.querySelector("[data-docs-save-goto]")?.classList.add("hidden")
      modal.classList.remove("hidden")
      return
    }
    this.approvalModalTarget.classList.remove("hidden")
  }
  closeApproval() { this.approvalModalTarget.classList.add("hidden") }
  closeDocsNeeded() { this.docsNeededModalTarget.classList.add("hidden") }
  closeFeeEmpty() { if (this.hasFeeEmptyModalTarget) this.feeEmptyModalTarget.classList.add("hidden") }

  // Live contract value from the fee form — schedule rows (typed value or
  // qty × unit price, like _recalcTotal) or the lump-sum amount. Used by the
  // approval-button gate so it never needs a page reload to catch up.
  _feeScheduleTotal() {
    if (!this.hasFeeFormTarget) return 0
    const mode = this.feeFormTarget.querySelector('[name="baseline_mode"]:checked')?.value
    if (mode !== "schedule") return this._num(this.feeFormTarget.querySelector('[name="amount"]')?.value)
    let total = 0
    if (!this.hasLinesBodyTarget) return 0
    this.linesBodyTarget.querySelectorAll("[data-fee-line]").forEach((row) => {
      const typed = this._num(row.querySelector("[data-line-value]")?.value)
      if (typed) { total += typed; return }
      const qty = this._num(row.querySelector('[name="lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="lines[][unit_price]"]')?.value)
      total += qty * price
    })
    return total
  }

  scrollToDocs() {
    this.closeDocsNeeded()
    // The invoice pop-up may be the thing covering the docs section.
    if (this.hasInvModalTarget) this.invModalTarget.classList.add("hidden")
    if (this.hasDocsSectionTarget) this.docsSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Sending an invoice with missing/expired documents gets the same
  // docs-needed pop-up as the fee approval request (Phelipe 2026-07-28).
  // Draft saves are exempt — the gate is armed only for send actions.
  guardInvoiceSend(e) {
    if (!this._invDocsGate) return
    const needed = [...this.element.querySelectorAll("[data-doc-row]")]
      .filter((r) => ["missing", "expired"].includes(r.dataset.docStatus))
    if (!needed.length) return
    e.preventDefault()
    if (!this.hasDocsNeededModalTarget) return
    const modal = this.docsNeededModalTarget
    const list = modal.querySelector("[data-docs-needed-list]")
    if (list) list.textContent = needed.map((r) => r.querySelector("span")?.textContent?.trim()).filter(Boolean).join(" · ")
    // Invoice context: the go-to-docs button saves their typing as a draft
    // first (Phelipe 2026-07-28) — plain "Take me there" would lose it.
    modal.querySelector("[data-docs-goto]")?.classList.add("hidden")
    modal.querySelector("[data-docs-save-goto]")?.classList.remove("hidden")
    modal.classList.remove("hidden")
  }

  // Save the invoice as a draft, then land on the documents section — the
  // redirect carries #portal-docs so the reload scrolls straight there.
  saveDraftAndGoToDocs() {
    const form = this.hasInvModalTarget ? this.invModalTarget.querySelector("form") : null
    if (!form) return
    const add = (name, value) => {
      const input = document.createElement("input")
      input.type = "hidden"; input.name = name; input.value = value
      form.appendChild(input)
    }
    add("save_draft", "1")
    add("goto_docs", "1")
    // form.submit() skips HTML validation (waiver etc.) — drafts don't need it.
    form.submit()
  }
  openWithdraw() { this.withdrawModalTarget.classList.remove("hidden") }
  closeWithdraw() { this.withdrawModalTarget.classList.add("hidden") }

  addApprover() {
    const row = document.createElement("div")
    row.className = "grid grid-cols-1 sm:grid-cols-2 gap-2"
    row.setAttribute("data-approver-row", "")
    row.innerHTML = `
      <input type="text" name="approvers[][name]" placeholder="Name" class="border border-gray-300 rounded-lg px-2.5 py-1.5 text-[13px]">
      <input type="email" name="approvers[][email]" placeholder="Email" class="border border-gray-300 rounded-lg px-2.5 py-1.5 text-[13px]">`
    this.approverRowsTarget.appendChild(row)
  }

  // Click a change order row: view; edit while the GC hasn't decided.
  openCoRow(e) {
    const d = e.currentTarget.dataset
    this._resetCo()
    const modal = this.coModalTarget
    const form = modal.querySelector("form")
    const editable = d.editable === "true"
    form.action = d.updateUrl
    // AI drop zone only on editable drafts (Phelipe 2026-07-28) — hidden
    // once it's pending signature or decided.
    modal.querySelector("[data-ai-zone]")?.classList.toggle("hidden", !(editable && d.signable === "true"))
    const set = (sel, v) => { const el = modal.querySelector(sel); if (el) { el.value = v || ""; el.disabled = !editable } }
    set('[name="title"]', d.title)
    set('[name="time_extension_days"]', d.days)
    set('[name="markup_pct"]', d.markupPct)
    set('[name="description"]', d.description)
    // PCO-style lines (Phelipe 2026-07-28) — seed from the row's JSON; a
    // legacy plain-amount CO becomes a single line at qty 1.
    let coLines = []
    try { coLines = JSON.parse(d.lines || "[]") } catch { /* view still works */ }
    if (!coLines.length && parseFloat(d.amount)) {
      coLines = [{ description: d.title || "Change", quantity: 1, unit: "", unit_price: parseFloat(d.amount) }]
    }
    this._seedCoLines(coLines, editable)
    const submit = modal.querySelector("[data-co-submit]")
    if (submit) { submit.textContent = "Save changes"; submit.classList.toggle("hidden", !editable) }
    const title = modal.querySelector("[data-co-title]")
    if (title) title.textContent = editable ? `Edit ${d.display || "change order"}` : (d.display || "Change order")
    const signBtn = modal.querySelector("[data-co-sign]")
    const signable = d.signable === "true"
    if (signBtn) signBtn.classList.toggle("hidden", !signable)
    this._signCtx = signable ? { url: d.signUrl, label: d.signLabel } : null
    this._setResend(this.hasCoResendBarTarget && this.coResendBarTarget, d)
    this._setPdfLink(modal.querySelector("[data-co-pdf]"), d.pdfUrl)
    modal.classList.remove("hidden")
  }

  _resetCo() {
    const modal = this.coModalTarget
    const form = modal.querySelector("form")
    if (form?.dataset.createUrl) form.action = form.dataset.createUrl
    modal.querySelector("[data-ai-zone]")?.classList.remove("hidden")
    const set = (sel) => { const el = modal.querySelector(sel); if (el) { el.value = ""; el.disabled = false } }
    set('[name="title"]'); set('[name="time_extension_days"]'); set('[name="markup_pct"]'); set('[name="description"]')
    this._seedCoLines([], true)
    const submit = modal.querySelector("[data-co-submit]")
    if (submit) { submit.textContent = "Send request"; submit.classList.remove("hidden") }
    const title = modal.querySelector("[data-co-title]")
    if (title) title.textContent = "Request a change order"
    const signBtn = modal.querySelector("[data-co-sign]")
    if (signBtn) signBtn.classList.add("hidden")
    if (this.hasCoResendBarTarget) this.coResendBarTarget.classList.add("hidden")
    this._setPdfLink(modal.querySelector("[data-co-pdf]"), null)
    this._signCtx = null
  }

  // ── PCO-style CO lines (Phelipe 2026-07-28): qty × unit price per row,
  // renumbered on every change; the CO total is the sum. ──
  addCoLine() {
    const modal = this.coModalTarget
    const body = modal.querySelector("[data-co-lines]")
    const tpl = modal.querySelector("[data-co-line-template]")
    if (body && tpl) { body.appendChild(tpl.content.cloneNode(true)); this._coRecalc() }
  }

  removeCoLine(e) {
    e.currentTarget.closest("[data-co-line]")?.remove()
    this._coRecalc()
  }

  coLineInput() { this._coRecalc() }

  // Seed the lines table — one blank row for a new request, the saved rows
  // for an edit (a plain read-only view gets everything disabled).
  _seedCoLines(lines, editable) {
    const modal = this.coModalTarget
    const body = modal.querySelector("[data-co-lines]")
    const tpl = modal.querySelector("[data-co-line-template]")
    if (!body || !tpl) return
    body.innerHTML = ""
    const rows = lines.length ? lines : [{}]
    rows.forEach((l) => {
      body.appendChild(tpl.content.cloneNode(true))
      const row = body.lastElementChild
      const fill = (name, v) => { const el = row.querySelector(`[name="change_lines[][${name}]"]`); if (el) { el.value = v ?? ""; el.disabled = !editable } }
      fill("item", l.item)
      fill("description", l.description)
      fill("quantity", l.quantity ? this._fmtQty(l.quantity) : "")
      fill("unit", l.unit)
      fill("unit_price", l.unit_price ? this._fmtMoney(l.unit_price) : "")
      const remove = row.querySelector("button")
      if (remove) remove.classList.toggle("hidden", !editable)
    })
    const add = modal.querySelector("[data-co-add-line]")
    if (add) add.classList.toggle("hidden", !editable)
    this._coRecalc()
  }

  _coRecalc() {
    const modal = this.coModalTarget
    let total = 0
    modal.querySelectorAll("[data-co-line]").forEach((row, i) => {
      const no = row.querySelector("[data-co-line-no]")
      if (no) no.textContent = i + 1
      const qty = this._num(row.querySelector('[name="change_lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="change_lines[][unit_price]"]')?.value)
      const line = qty * price
      const cell = row.querySelector("[data-co-line-total]")
      if (cell) cell.textContent = `$${this._fmtMoney(line)}`
      total += line
    })
    const markup = this._num(modal.querySelector('[name="markup_pct"]')?.value)
    const totalCell = modal.querySelector("[data-co-total]")
    if (totalCell) totalCell.textContent = `$${this._fmtMoney(total + total * markup / 100)}`
  }

  _fmtQty(v) {
    const n = parseFloat(v) || 0
    return Number.isInteger(n) ? String(n) : String(n)
  }

  // ── Sign & send for signature (Phelipe 2026-07-27) — shared modal for
  // pay applications and change orders; the row that opened the document
  // modal stashed the sign URL + label in _signCtx. ──
  openSignModal() {
    if (!this._signCtx || !this.hasSignModalTarget) return
    this.signModalTarget.querySelectorAll("[data-sign-label]").forEach((el) => { el.textContent = this._signCtx.label })
    if (this.hasSignFormTarget) this.signFormTarget.action = this._signCtx.url
    // The Subcontractor's Certification applies to pay requests only
    // (Phelipe 2026-08-18) — change orders sign without it.
    if (this.hasSignCertTarget) {
      const pay = (this._signCtx.url || "").includes("/invoice/")
      this.signCertTarget.classList.toggle("hidden", !pay)
      if (this.hasSignCertAgreeTarget) {
        this.signCertAgreeTarget.required = pay
        this.signCertAgreeTarget.checked = false
      }
    }
    this.signModalTarget.classList.remove("hidden")
  }

  closeSignModal() { if (this.hasSignModalTarget) this.signModalTarget.classList.add("hidden") }

  addSignRow() {
    if (this.hasSignRowsTarget && this.hasSignRowTemplateTarget) {
      this.signRowsTarget.appendChild(this.signRowTemplateTarget.content.cloneNode(true))
    }
  }

  removeSignRow(e) { e.currentTarget.closest("[data-sign-row]")?.remove() }

  // ── Pay app Qty / $ / % entry (Phelipe 2026-07-28): type any one and the
  // other two follow; the balance cell shows dollars and percent left. ────
  payAppInput(e) {
    const row = e?.currentTarget?.closest?.("[data-app-row]")
    if (row) this._syncAppRow(row, "amount")
    else this.payAppModalTarget.querySelectorAll("[data-app-row]").forEach((r) => this._syncAppRow(r, "amount"))
    this._recalcAppTotals()
  }

  payAppQty(e) {
    this._syncAppRow(e.currentTarget.closest("[data-app-row]"), "qty")
    this._recalcAppTotals()
  }

  payAppPct(e) {
    this._syncAppRow(e.currentTarget.closest("[data-app-row]"), "pct")
    this._recalcAppTotals()
  }

  _syncAppRow(row, source) {
    if (!row) return
    const sched = parseFloat(row.dataset.scheduled) || 0
    const up = parseFloat(row.dataset.unitPrice) || 0
    const qtyEl = row.querySelector("[data-app-qty]")
    const amtEl = row.querySelector('[name="app_lines[][amount]"]')
    const pctEl = row.querySelector("[data-app-pct]")
    let amount = this._num(amtEl?.value)
    if (source === "qty") {
      amount = this._num(qtyEl?.value) * up
      if (amtEl) amtEl.value = qtyEl?.value?.trim() ? this._fmtMoney(amount) : ""
    } else if (source === "pct") {
      amount = (this._num(pctEl?.value) / 100) * sched
      if (amtEl) amtEl.value = pctEl?.value?.trim() ? this._fmtMoney(amount) : ""
    }
    const blank = !(amtEl?.value || "").trim()
    if (source !== "qty" && qtyEl && !qtyEl.hasAttribute("data-qty-locked")) {
      qtyEl.value = blank || up <= 0 ? "" : String(Math.round((amount / up) * 1000) / 1000)
    }
    if (source !== "pct" && pctEl) {
      pctEl.value = blank || sched <= 0 ? "" : String(Math.round((amount / sched) * 1000) / 10)
    }
  }

  _recalcAppTotals() {
    let total = 0
    this.payAppModalTarget.querySelectorAll("[data-app-row]").forEach((row) => {
      const sched = parseFloat(row.dataset.scheduled) || 0
      const amount = this._num(row.querySelector('[name="app_lines[][amount]"]')?.value)
      const balance = sched - (parseFloat(row.dataset.previous) || 0) - amount
      const amt = row.querySelector("[data-app-balance-amt]")
      if (amt) {
        amt.textContent = `$${this._fmtMoney(balance)}`
        amt.classList.toggle("text-red-600", balance < -0.001)
        amt.classList.toggle("text-gray-500", balance >= -0.001)
      }
      const pct = row.querySelector("[data-app-balance-pct]")
      if (pct) pct.textContent = sched > 0 ? `${Math.round((balance / sched) * 1000) / 10}%` : ""
      total += amount
    })
    const totalCell = this.payAppModalTarget.querySelector("[data-app-total]")
    if (totalCell) totalCell.textContent = `$${this._fmtMoney(total)}`
    // Retainage preview (item 24, 2026-07-28) — display only, matching the
    // approval-time hold math in SubInvoice#approve!: (amt × pct / 100)
    // rounded to cents. The rows render only when the contract holds
    // retainage, so the cells simply aren't there otherwise.
    const retCell = this.payAppModalTarget.querySelector("[data-app-retainage]")
    const netCell = this.payAppModalTarget.querySelector("[data-app-net]")
    if (retCell || netCell) {
      const pct = parseFloat(this.payAppModalTarget.querySelector("[data-retainage-pct]")?.dataset?.retainagePct) || 0
      const retainage = Math.round(total * pct) / 100
      if (retCell) retCell.textContent = `$${this._fmtMoney(retainage)}`
      if (netCell) netCell.textContent = `$${this._fmtMoney(total - retainage)}`
    }
  }

  disconnect() {
    clearTimeout(this._feeSaveTimer)
    if (this._dropGuard) {
      document.removeEventListener("dragover", this._dropGuard)
      document.removeEventListener("drop", this._dropGuard)
      this._dropGuard = null
    }
  }

  connect() {
    this._guardStrayDrops()
    // Line items are the default view (Phelipe 2026-07-23) — greet the sub
    // with a few blank rows instead of an empty table.
    if (this.hasScheduleWrapTarget && !this.scheduleWrapTarget.classList.contains("hidden") &&
        this.hasLinesBodyTarget && !this.linesBodyTarget.querySelector("[data-fee-line]")) {
      for (let i = 0; i < 3; i++) this.addLine()
    }
    // As-needed invoice breakdown (Phelipe 2026-07-28): the first line is
    // already open, ready to type into.
    if (this.hasInvLinesBodyTarget && !this.invLinesBodyTarget.querySelector("[data-inv-line]")) {
      this.addInvLine()
    }
  }

  // In-app the getting-set-up checklist hides behind a "Getting set up"
  // link in the card header (Phelipe 2026-08-19) — members who know the
  // flow never see it; anyone lost opens it here.
  toggleSteps() {
    if (this.hasStepsPanelTarget) this.stepsPanelTarget.classList.toggle("hidden")
  }

  // ── Portal tabs (Phelipe 2026-07-23): Fee Schedule / Pay Applications /
  // Change Orders for fee-schedule subs. The tokened portal only — in-app
  // the finance tab row navigates with real links. ───────────────────────
  showTab(e) {
    const key = e.currentTarget.dataset.tab
    this.element.querySelectorAll("[data-portal-tab]").forEach((s) => {
      s.classList.toggle("hidden", s.dataset.portalTab !== key)
    })
    e.currentTarget.closest("[data-default-tab]").querySelectorAll("[data-tab]").forEach((b) => {
      const active = b.dataset.tab === key
      b.classList.toggle("text-brand", active)
      b.classList.toggle("font-bold", active)
      b.classList.toggle("text-gray-500", !active)
      b.classList.toggle("font-medium", !active)
    })
  }

  // ── Sign a requested lien waiver in place (Phelipe 2026-08-20): the
  // pay-application sign modal's shape, minus the extra-signers chain.
  // The clicked row carries the payment facts; the modal's data attributes
  // carry the i18n templates with %{amount}/%{date} slots.
  openSignWaiver(e) {
    const d = e.currentTarget.dataset
    const m = this.signWaiverModalTarget
    const fill = (tpl) => (tpl || "").replaceAll("%{amount}", d.amount).replaceAll("%{date}", d.date)
    m.querySelector("[data-sw-id]").value = d.waiverId
    m.querySelector("[data-sw-type]").textContent = `— ${d.type}`
    m.querySelector("[data-sw-payment]").textContent = fill(m.dataset.paymentLine)
    m.querySelector("[data-sw-body]").textContent =
      fill(d.conditional === "1" ? m.dataset.conditionalBody : m.dataset.unconditionalBody)
    m.querySelector("[data-sw-agree]").checked = false
    m.classList.remove("hidden")
    this.signWaiverCheck()
  }

  closeSignWaiver() { this.signWaiverModalTarget.classList.add("hidden") }

  signWaiverCheck() {
    const m = this.signWaiverModalTarget
    const ok = m.querySelector("[data-sw-agree]").checked &&
               m.querySelector("[data-sw-name]").value.trim().length > 0
    m.querySelector("[data-sw-submit]").disabled = !ok
  }

  // Nudge whoever a formal CO's signature round waits on (parity audit CO4)
  // — a plain form POST; the action redirects back with its flash.
  resendDocSignature(e) {
    const form = document.createElement("form")
    form.method = "POST"
    form.action = e.currentTarget.dataset.url
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    if (csrf) {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "authenticity_token"
      input.value = csrf
      form.appendChild(input)
    }
    document.body.appendChild(form)
    form.submit()
  }

  async fileChosen(e) {
    const input = e.currentTarget
    const file = input.files[0]
    if (!file) return
    const form = input.closest("form")
    const label = form.querySelector("[data-file-label]")
    const submit = form.querySelector("[data-submit]")
    label.textContent = `Uploading ${file.name}…`
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      form.querySelector("[data-cache-input]").value = u.cache_name
      form.querySelector("[data-filename-input]").value = u.filename
      label.textContent = `✓ ${file.name}`
      if (submit) submit.disabled = false
    } catch (err) {
      label.textContent = err.message
      input.value = ""
    }
  }

  // ── Fee schedule (item 31): lump sum ⇄ line-item breakdown ────────────
  // Switching modes with data on the other side warns first (Phelipe
  // 2026-07-25): Continue erases it, Cancel reverts the radio.
  modeChanged(e) {
    const target = e.currentTarget.value
    const losing = target === "schedule"
      ? this._num(this.feeFormTarget?.querySelector('[name="amount"]')?.value)
      : this._linesHaveContent()
    if (losing && this.hasModeSwitchModalTarget) {
      this._pendingMode = target
      const other = this.feeFormTarget.querySelector(`[name="baseline_mode"][value="${target === "schedule" ? "lump_sum" : "schedule"}"]`)
      if (other) other.checked = true
      const text = this.modeSwitchModalTarget.querySelector("[data-mode-switch-text]")
      if (text) {
        text.textContent = target === "lump_sum"
          ? "Switching to lump sum erases your line items."
          : "Switching to line items erases your lump-sum amount."
      }
      this.modeSwitchModalTarget.classList.remove("hidden")
      return
    }
    this._applyMode(target)
  }

  cancelModeSwitch() { this.modeSwitchModalTarget.classList.add("hidden") }

  async confirmModeSwitch() {
    this.modeSwitchModalTarget.classList.add("hidden")
    const mode = this._pendingMode
    // Erase server-side right away: an empty schedule save wipes the lines
    // AND zeroes the lump amount.
    try {
      const fd = new FormData()
      const tok = this.feeFormTarget.querySelector('[name="authenticity_token"]')
      if (tok) fd.append("authenticity_token", tok.value)
      fd.append("baseline_mode", "schedule")
      await fetch(this.feeFormTarget.action, { method: "POST", headers: { "Accept": "application/json" }, body: fd })
    } catch { /* the next autosave settles it */ }
    this.linesBodyTarget?.querySelectorAll("[data-fee-line]").forEach((r) => r.remove())
    const amountEl = this.feeFormTarget.querySelector('[name="amount"]')
    if (amountEl) amountEl.value = ""
    const radio = this.feeFormTarget.querySelector(`[name="baseline_mode"][value="${mode}"]`)
    if (radio) radio.checked = true
    this._applyMode(mode)
    if (this.hasFeeSaveStatusTarget) this.feeSaveStatusTarget.textContent = "Cleared — changes save automatically"
  }

  _linesHaveContent() {
    if (!this.hasLinesBodyTarget) return false
    return [...this.linesBodyTarget.querySelectorAll("[data-fee-line]")].some((row) => {
      const g = (n) => row.querySelector(`[name="lines[][${n}]"]`)?.value?.trim() || ""
      return g("description") !== "" || this._num(g("scheduled_value")) || this._num(g("unit_price"))
    })
  }

  _applyMode(mode) {
    const schedule = mode === "schedule"
    if (this.hasLumpWrapTarget) this.lumpWrapTarget.classList.toggle("hidden", schedule)
    if (this.hasScheduleWrapTarget) this.scheduleWrapTarget.classList.toggle("hidden", !schedule)
    if (schedule && this.hasLinesBodyTarget && !this.linesBodyTarget.querySelector("[data-fee-line]")) {
      for (let i = 0; i < 3; i++) this.addLine()
    }
    this._recalcTotal()
  }

  // ── Required document upload from the portal (Phelipe 2026-07-25):
  // presign → PUT → register on the doc slot; pill flips live. ───────────
  async complianceFileChosen(e) {
    const input = e.target
    const file = input.files[0]
    if (!file) return
    input.value = ""
    this._uploadCompliance(input.closest("[data-doc-row]"), file)
  }

  // ── Drag and drop onto a document slot (Larry K1-40, 2026-09-23) ────────
  // He asked why he had to hunt for the file. The row itself is the dropzone
  // and highlights inline — no full-page overlay, same as Photos.
  _hasFiles(e) { return !!e.dataTransfer && [...e.dataTransfer.types].includes("Files") }

  // A file dropped anywhere else on the portal would otherwise open in the tab
  // and throw the sub off the page. Swallow it; only the rows accept files.
  _guardStrayDrops() {
    if (this._dropGuard) return
    this._dropGuard = e => { if (this._hasFiles(e)) e.preventDefault() }
    document.addEventListener("dragover", this._dropGuard)
    document.addEventListener("drop", this._dropGuard)
  }

  docDragOver(e) {
    if (!this._hasFiles(e)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = "copy"
    e.currentTarget.classList.add("border-brand", "bg-brand/5")
  }

  docDragLeave(e) {
    const row = e.currentTarget
    if (e.relatedTarget && row.contains(e.relatedTarget)) return
    row.classList.remove("border-brand", "bg-brand/5")
  }

  docDrop(e) {
    if (!this._hasFiles(e)) return
    e.preventDefault()
    const row = e.currentTarget
    row.classList.remove("border-brand", "bg-brand/5")
    const file = e.dataTransfer.files[0]
    if (file) this._uploadCompliance(row, file)
  }

  async _uploadCompliance(row, file) {
    if (!row || !file) return
    const pill = row.querySelector("[data-doc-pill]")
    const setPill = (text, cls) => {
      pill.textContent = text
      pill.className = `inline-flex px-2 py-0.5 rounded-full text-[13px] font-medium ${cls}`
    }
    setPill("Uploading…", "bg-blue-100 text-blue-700")
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      setPill("Checking with AI…", "bg-purple-100 text-purple-700")
      const resp = await fetch(row.dataset.uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ cache_name: u.cache_name, filename: u.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save the document")
      const issuesEl = row.querySelector("[data-doc-issues]")
      const btn = row.querySelector("[data-doc-btn-label]")
      if (btn) btn.textContent = "Replace"
      if (data.issues && data.issues.length) {
        // AI flagged a problem — the slot stays non-compliant until a good
        // file replaces it (Phelipe 2026-07-25).
        setPill("Needs attention", "bg-red-100 text-red-700")
        row.dataset.docStatus = "missing"
        if (issuesEl) { issuesEl.textContent = data.issues.join(" "); issuesEl.className = "w-full text-[13px] text-red-600" }
      } else {
        setPill("Verified", "bg-green-100 text-green-700")
        row.dataset.docStatus = "valid"
        if (issuesEl) {
          if (data.expires_on) {
            issuesEl.textContent = `Read automatically — expires ${data.expires_on}`
            issuesEl.className = "w-full text-[13px] text-gray-400"
          } else {
            issuesEl.textContent = ""
            issuesEl.className = "hidden"
          }
        }
      }
    } catch (err) {
      setPill(err.message, "bg-red-100 text-red-700")
    }
  }

  // A step in the rail jumps to its tab (Phelipe 2026-07-25).
  stepJump(e) {
    const key = e.currentTarget.dataset.stepTab
    if (!key) return
    this.element.querySelector(`[data-default-tab] [data-tab="${key}"]`)?.click()
  }

  // ── Compliance exception (Phelipe 2026-07-25): no file / bad AI read →
  // explain instead; the GC reviews. ─────────────────────────────────────
  openDocException(e) {
    this._exceptionRow = e.currentTarget.closest("[data-doc-row]")
    const label = this.docExceptionModalTarget.querySelector("[data-exception-doc-label]")
    if (label) label.textContent = e.currentTarget.dataset.docLabel || "this document"
    this.exceptionReasonTarget.value = ""
    this.exceptionErrorTarget.classList.add("hidden")
    this.docExceptionModalTarget.classList.remove("hidden")
  }
  closeDocException() { this.docExceptionModalTarget.classList.add("hidden") }

  async submitDocException() {
    const row = this._exceptionRow
    if (!row) return
    const reason = this.exceptionReasonTarget.value.trim()
    if (!reason) {
      this.exceptionErrorTarget.textContent = "Add a short reason first."
      this.exceptionErrorTarget.classList.remove("hidden")
      return
    }
    try {
      const resp = await fetch(`${row.dataset.uploadUrl}/exception`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ reason })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't send the exception")
      this.closeDocException()
      row.dataset.docStatus = "exception"
      const pill = row.querySelector("[data-doc-pill]")
      if (pill) {
        pill.textContent = "Exception filed — under review"
        pill.className = "inline-flex px-2 py-0.5 rounded-full text-[13px] font-medium bg-amber-100 text-amber-700"
      }
      const issuesEl = row.querySelector("[data-doc-issues]")
      if (issuesEl) { issuesEl.textContent = `Your note: "${data.reason}"`; issuesEl.className = "w-full text-[13px] text-amber-700" }
    } catch (err) {
      this.exceptionErrorTarget.textContent = err.message
      this.exceptionErrorTarget.classList.remove("hidden")
    }
  }

  // ── Docs-only teammate invite (Larry 2026-07-28): the sub emails whoever
  // actually holds the COI / W-9 a link that opens ONLY the documents
  // page — no fee schedule, pay apps, or change orders. ──────────────────
  openDocsInvite() {
    if (!this.hasDocsInviteModalTarget) return
    const d = this.hasDocsInviteAreaTarget ? this.docsInviteAreaTarget.dataset : {}
    if (this.hasDocsInviteNameTarget) this.docsInviteNameTarget.value = d.inviteName || ""
    if (this.hasDocsInviteEmailTarget) this.docsInviteEmailTarget.value = d.inviteEmail || ""
    if (this.hasDocsInviteErrorTarget) this.docsInviteErrorTarget.classList.add("hidden")
    this.docsInviteModalTarget.classList.remove("hidden")
  }

  closeDocsInvite() { if (this.hasDocsInviteModalTarget) this.docsInviteModalTarget.classList.add("hidden") }

  async submitDocsInvite() {
    const name = this.hasDocsInviteNameTarget ? this.docsInviteNameTarget.value.trim() : ""
    const email = this.hasDocsInviteEmailTarget ? this.docsInviteEmailTarget.value.trim() : ""
    if (!email || !email.includes("@")) {
      if (this.hasDocsInviteErrorTarget) {
        this.docsInviteErrorTarget.textContent = "Enter their email address."
        this.docsInviteErrorTarget.classList.remove("hidden")
      }
      return
    }
    const btn = this.hasDocsInviteSubmitTarget ? this.docsInviteSubmitTarget : null
    if (btn) btn.disabled = true
    try {
      const data = await this._postDocsInvite(name, email)
      this.closeDocsInvite()
      this._renderDocsInviteChip(data.name)
      this._showToast(`Invite sent to ${data.name} — they got a link to upload the documents.`)
    } catch (err) {
      if (this.hasDocsInviteErrorTarget) {
        this.docsInviteErrorTarget.textContent = err.message
        this.docsInviteErrorTarget.classList.remove("hidden")
      }
    } finally {
      if (btn) btn.disabled = false
    }
  }

  // Resend re-sends the same link to the person on the chip — the server
  // never rotates the docs token on invite, so earlier emails keep working.
  async resendDocsInvite(e) {
    if (!this.hasDocsInviteAreaTarget) return
    const area = this.docsInviteAreaTarget
    const btn = e.currentTarget
    btn.disabled = true
    try {
      const data = await this._postDocsInvite(area.dataset.inviteName || "", area.dataset.inviteEmail || "")
      this._showToast(`Invite re-sent to ${data.name}.`)
    } catch (err) {
      this._showToast(err.message, "error")
    } finally {
      btn.disabled = false
    }
  }

  async _postDocsInvite(name, email) {
    const area = this.docsInviteAreaTarget
    const resp = await fetch(area.dataset.inviteUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json",
                 "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
      body: JSON.stringify({ name, email })
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok) throw new Error(data.error || "Couldn't send the invite — try again.")
    area.dataset.inviteName = name
    area.dataset.inviteEmail = email
    return data
  }

  // Swap the affordance for the "Invited: <name> · Resend" chip — same
  // markup the partial server-renders on the next load.
  _renderDocsInviteChip(name) {
    if (!this.hasDocsInviteAreaTarget) return
    const area = this.docsInviteAreaTarget
    area.innerHTML = ""
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700"
    chip.setAttribute("data-docs-invite-chip", "")
    const label = document.createElement("span")
    label.textContent = "Invited:"
    const who = document.createElement("span")
    who.className = "font-medium"
    who.textContent = name
    const resend = document.createElement("button")
    resend.type = "button"
    resend.className = "text-brand font-medium hover:underline cursor-pointer"
    resend.setAttribute("data-action", "click->sub-portal#resendDocsInvite")
    resend.textContent = "Resend"
    chip.append(label, who, resend)
    const other = document.createElement("button")
    other.type = "button"
    other.className = "text-gray-400 hover:text-gray-600 underline decoration-dotted cursor-pointer"
    other.setAttribute("data-action", "click->sub-portal#openDocsInvite")
    other.textContent = "Invite someone else"
    area.append(chip, other)
  }

  // ── Bid upload → AI fills the fee schedule (Phelipe 2026-07-24) ────────
  clickBidZone() { this.bidFileInputTarget.click() }
  dragOverBidZone(e) {
    e.preventDefault()
    this.bidDropZoneTarget.classList.add("border-purple-500", "bg-purple-100")
  }
  dragLeaveBidZone() { this.bidDropZoneTarget.classList.remove("border-purple-500", "bg-purple-100") }
  dropOnBidZone(e) {
    e.preventDefault()
    this.dragLeaveBidZone()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._uploadBid(file)
  }
  bidFileChosen(e) {
    const file = e.target.files[0]
    if (file) this._uploadBid(file)
    e.target.value = ""
  }

  async _uploadBid(file) {
    const status = this.bidReadStatusTarget
    const readUrl = this.bidDropZoneTarget.closest("[data-bid-read-url]").dataset.bidReadUrl
    status.textContent = `Uploading ${file.name}…`
    status.classList.remove("hidden")
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")

      status.textContent = "✨ Reading your bid…"
      const resp = await fetch(readUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ cache_name: u.cache_name, filename: u.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the bid")

      this.linesBodyTarget.querySelectorAll("[data-fee-line]").forEach((r) => r.remove())
      data.lines.forEach((line, i) => {
        this.addLine()
        const row = this.linesBodyTarget.querySelectorAll("[data-fee-line]")[i]
        const set = (name, v) => { const el = row.querySelector(`[name="lines[][${name}]"]`); if (el && v != null && v !== "") el.value = v }
        set("item", line.item || String(i + 1))
        set("description", line.description)
        set("quantity", line.quantity)
        set("unit", line.unit)
        set("unit_price", line.unit_price != null ? this._fmtMoney(line.unit_price) : null)
        set("scheduled_value", line.value != null ? this._fmtMoney(line.value) : null)
      })
      this._recalcTotal()
      this._queueFeeSave()
      // Green check when the extracted lines add up to the total printed on
      // the bid file (Phelipe 2026-07-24); amber heads-up when they don't.
      const sum = data.lines.reduce((t, l) => t + (l.value != null ? l.value : (l.quantity || 0) * (l.unit_price || 0)), 0)
      const n = data.lines.length
      const plural = n === 1 ? "" : "s"
      status.classList.remove("text-purple-700", "text-green-600", "text-amber-600")
      if (data.document_total != null && Math.abs(sum - data.document_total) < 0.02) {
        status.classList.add("text-green-600")
        status.textContent = `✓ Filled ${n} line${plural} — $${this._fmtMoney(sum)} matches your bid file. File attached as backup.`
      } else if (data.document_total != null) {
        status.classList.add("text-amber-600")
        status.textContent = `Filled ${n} line${plural} — lines add up to $${this._fmtMoney(sum)} but the file shows $${this._fmtMoney(data.document_total)}. Double-check below. File attached as backup.`
      } else {
        status.classList.add("text-purple-700")
        status.textContent = `✓ Filled ${n} line${plural} from your bid — review below. File attached as backup.`
      }
    } catch (err) {
      status.classList.remove("text-green-600", "text-amber-600")
      status.classList.add("text-purple-700")
      status.textContent = err.message
    }
  }

  // ── AI drop zones (Phelipe 2026-07-28): same tech as the bid reader for
  // the pay application, change order, and as-needed invoice forms — drop a
  // file → presign → S3 PUT → AI OCR-reads it → the form fills itself, and
  // the file rides as the submission's backup attachment. ─────────────────
  clickAiZone(e) { e.currentTarget.closest("[data-ai-zone]")?.querySelector("[data-ai-zone-input]")?.click() }

  dragOverAiZone(e) {
    e.preventDefault()
    e.currentTarget.closest("[data-ai-zone]")?.querySelector("[data-ai-zone-box]")?.classList.add("border-purple-500", "bg-purple-100")
  }

  dragLeaveAiZone(e) {
    e.currentTarget.closest("[data-ai-zone]")?.querySelector("[data-ai-zone-box]")?.classList.remove("border-purple-500", "bg-purple-100")
  }

  dropOnAiZone(e) {
    e.preventDefault()
    this.dragLeaveAiZone(e)
    const file = e.dataTransfer?.files?.[0]
    if (file) this._runAiZone(e.currentTarget.closest("[data-ai-zone]"), file)
  }

  aiZoneFileChosen(e) {
    const zone = e.currentTarget.closest("[data-ai-zone]")
    const file = e.target.files[0]
    if (file) this._runAiZone(zone, file)
    e.target.value = ""
  }

  async _runAiZone(zone, file) {
    if (!zone) return
    const status = zone.querySelector("[data-ai-zone-status]")
    const say = (text, cls = "text-purple-700") => {
      if (!status) return
      status.className = `text-[13px] ${cls} mt-1.5`
      status.textContent = text
    }
    say(`Uploading ${file.name}…`)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type || "application/octet-stream", byte_size: file.size }] })
      })
      const pd = await presign.json()
      if (!presign.ok) throw new Error(pd.error || "Upload refused")
      const u = pd.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")

      say("✨ Reading your file…")
      const resp = await fetch(zone.dataset.aiReadUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
                   "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content },
        body: JSON.stringify({ cache_name: u.cache_name, filename: u.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the file")

      const form = zone.closest("form")
      if (zone.dataset.aiZone === "payapp") this._fillPayAppFromAi(data)
      else if (zone.dataset.aiZone === "co") this._fillCoFromAi(data)
      else this._fillInvoiceFromAi(form, data)

      // The file rides as the submission's backup — same hidden inputs the
      // Attach button fills.
      if (form) {
        const cache = form.querySelector("[data-cache-input]")
        const name = form.querySelector("[data-filename-input]")
        if (cache) cache.value = u.cache_name
        if (name) name.value = u.filename
        const label = form.querySelector("[data-file-label]")
        if (label) label.textContent = `✓ ${file.name}`
      }
      say(`✓ Filled from ${file.name} — review below. File attached as backup.`, "text-green-600")
    } catch (err) {
      say(err.message)
    }
  }

  // Per-line this-period amounts keyed to the pay app rows, then number and
  // period — the sub reviews before submitting.
  _fillPayAppFromAi(data) {
    const modal = this.payAppModalTarget
    ;(data.amounts || []).forEach((a) => {
      const row = modal.querySelector(`[data-app-row][data-line-key="${a.key}"]`)
      const input = row?.querySelector('[name="app_lines[][amount]"]')
      if (input && !input.disabled) input.value = this._fmtMoney(a.amount)
    })
    const num = modal.querySelector("[data-app-number]")
    if (num && data.number && !num.disabled) num.value = data.number
    const per = modal.querySelector("[data-app-period]")
    if (per && data.period_end && !per.disabled) per.value = data.period_end
    this.payAppInput()
  }

  _fillCoFromAi(data) {
    const modal = this.coModalTarget
    const set = (sel, v) => { const el = modal.querySelector(sel); if (el && v != null && v !== "" && !el.disabled) el.value = v }
    set('[name="title"]', data.title)
    set('[name="time_extension_days"]', data.time_extension_days)
    set('[name="description"]', data.description)
    if ((data.lines || []).length) this._seedCoLines(data.lines, true)
  }

  // Number, period, and the breakdown grid for the as-needed invoice form —
  // the invoice's own line items when the AI found them, else one lump line
  // for the total.
  _fillInvoiceFromAi(form, data) {
    const numberEl = form?.querySelector("[data-invoice-number]")
    const periodEl = form?.querySelector("[data-invoice-period]")
    if (numberEl && data.number) numberEl.value = data.number
    if (periodEl && data.period_end) periodEl.value = data.period_end
    if (!this.hasInvLinesBodyTarget) return
    const lines = (data.lines || []).length ? data.lines
      : (data.amount ? [{ description: "Work performed", quantity: 1, unit: "LS", unit_price: data.amount }] : [])
    if (!lines.length) return
    this.invLinesBodyTarget.querySelectorAll("[data-inv-line]").forEach((r) => r.remove())
    lines.forEach((line, i) => {
      this.addInvLine()
      const row = this.invLinesBodyTarget.querySelectorAll("[data-inv-line]")[i]
      const set = (name, v) => { const el = row.querySelector(`[name="app_lines[][${name}]"]`); if (el && v != null && v !== "") el.value = v }
      set("item", line.item || String(i + 1))
      set("description", line.description)
      set("quantity", line.quantity)
      set("unit", line.unit)
      set("unit_price", line.unit_price != null ? this._fmtMoney(line.unit_price) : null)
      const cell = row.querySelector("[data-inv-line-total]")
      if (cell) cell.textContent = `$${this._fmtMoney((line.quantity || 0) * (line.unit_price || 0))}`
    })
    this._recalcInvTotal()
  }

  addLine() {
    if (!this.hasLinesBodyTarget) return
    const count = this.linesBodyTarget.querySelectorAll("[data-fee-line]").length
    const tr = document.createElement("tr")
    tr.setAttribute("data-fee-line", "")
    const cell = (name, extra = "", cls = "") =>
      `<td class="py-1 pr-3"><input type="text" name="lines[][${name}]" ${extra} class="w-full border border-gray-300 rounded px-1.5 py-1 text-[13px] ${cls}"></td>`
    const moneyCell = (name, extra = "") =>
      `<td class="py-1 pr-3"><div class="relative"><span class="absolute left-1.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400 pointer-events-none">$</span><input type="text" name="lines[][${name}]" ${extra} class="w-full border border-gray-300 rounded pl-4 pr-1.5 py-1 text-right text-[13px]"></div></td>`
    tr.innerHTML = `
      ${cell("item", `value="${count + 1}"`)}
      ${cell("description")}
      ${cell("quantity", 'data-action="input->sub-portal#lineInput"', "text-right")}
      ${cell("unit")}
      ${moneyCell("unit_price", 'data-action="input->sub-portal#lineInput blur->sub-portal#moneyBlur"')}
      ${moneyCell("scheduled_value", 'data-action="input->sub-portal#lineInput blur->sub-portal#moneyBlur" data-line-value placeholder="auto"')}
      <td class="py-1 text-right"><button type="button" data-action="click->sub-portal#removeLine" class="text-gray-300 hover:text-red-500 cursor-pointer" tabindex="-1">✕</button></td>`
    this.linesBodyTarget.appendChild(tr)
  }

  removeLine(e) {
    e.currentTarget.closest("[data-fee-line]")?.remove()
    this._recalcTotal()
    // Deletions must persist even when the grid goes empty — bypass the
    // skip-empty guard for this save (Phelipe 2026-07-24).
    this._feeDeleted = true
    this._queueFeeSave()
  }

  // ── As-needed invoice pop-up (Phelipe 2026-07-28): list + modal like the
  // Pay Applications page — the same modal serves new / edit / read-only. ─
  openInvModal() { this._resetInvModal(); this.invModalTarget.classList.remove("hidden") }
  closeInvModal() { if (this.hasInvModalTarget) this.invModalTarget.classList.add("hidden") }

  openInvRow(e) {
    const d = e.currentTarget.dataset
    this._resetInvModal()
    const modal = this.invModalTarget
    const form = modal.querySelector("form")
    const editable = d.editable === "true"
    const signable = d.signable === "true"
    form.action = d.updateUrl
    const num = form.querySelector("[data-invoice-number]")
    if (num) { num.value = d.number || ""; num.disabled = !editable }
    const per = form.querySelector("[data-invoice-period]")
    if (per) { per.value = d.periodEnd || ""; per.disabled = !editable }
    let lines = []
    try { lines = JSON.parse(d.lines || "[]") } catch { /* view still works */ }
    // Legacy amount-only invoices become a single lump line.
    if (!lines.length && parseFloat(d.amount)) {
      lines = [{ description: "Work performed", quantity: 1, unit: "LS", unit_price: parseFloat(d.amount) }]
    }
    this._fillInvGrid(lines, editable)
    // Drafts still need the waiver tick when they go out (Phelipe
    // 2026-07-28); submitted ones signed it already — no re-tick.
    const isDraft = d.status === "draft"
    const waiver = modal.querySelector("[data-inv-waiver]")
    const check = waiver?.querySelector('input[type="checkbox"]')
    if (waiver) waiver.classList.toggle("hidden", !isDraft)
    if (check) { check.required = isDraft; check.checked = !isDraft }
    const submit = modal.querySelector("[data-inv-submit]")
    if (submit) {
      submit.textContent = isDraft ? "Sign & send for approval" : "Save changes"
      // send_now flips the draft to received + starts the signature round.
      if (isDraft) { submit.name = "send_now"; submit.value = "1" } else { submit.removeAttribute("name") }
      submit.classList.toggle("hidden", !editable)
    }
    const draftBtn = modal.querySelector("[data-inv-draft]")
    if (draftBtn) { draftBtn.textContent = "Save draft"; draftBtn.classList.toggle("hidden", !(editable && isDraft)) }
    // Docs gate armed only when this button SENDS (new or draft) — a plain
    // "Save changes" on a submitted invoice doesn't need paperwork.
    this._invDocsGate = isDraft
    const title = modal.querySelector("[data-inv-title]")
    if (title) title.textContent = editable ? `Edit ${d.display || "invoice"}` : (d.display || "Invoice")
    modal.querySelector("[data-ai-zone]")?.classList.toggle("hidden", !(editable && (signable || isDraft)))
    modal.querySelector("[data-inv-attach]")?.classList.toggle("hidden", !editable)
    const hist = modal.querySelector("[data-inv-history]")
    if (hist) {
      hist.innerHTML = ""
      const tpl = document.querySelector(`template[data-inv-history-for="${d.invoiceId}"]`)
      if (tpl) hist.appendChild(tpl.content.cloneNode(true))
      hist.classList.toggle("hidden", !tpl)
    }
    const signBtn = modal.querySelector("[data-inv-sign]")
    if (signBtn) signBtn.classList.toggle("hidden", !signable)
    this._signCtx = signable ? { url: d.signUrl, label: d.signLabel } : null
    this._setResend(this.hasInvResendBarTarget && this.invResendBarTarget, d)
    this._setPdfLink(modal.querySelector("[data-inv-pdf]"), d.pdfUrl)
    modal.classList.remove("hidden")
  }

  _resetInvModal() {
    const modal = this.invModalTarget
    const form = modal.querySelector("form")
    if (form?.dataset.createUrl) form.action = form.dataset.createUrl
    const num = form.querySelector("[data-invoice-number]")
    if (num) { num.value = ""; num.disabled = false }
    const per = form.querySelector("[data-invoice-period]")
    if (per) { per.value = ""; per.disabled = false }
    this._fillInvGrid([], true)
    const waiver = modal.querySelector("[data-inv-waiver]")
    const check = waiver?.querySelector('input[type="checkbox"]')
    if (waiver) waiver.classList.remove("hidden")
    if (check) { check.required = true; check.checked = false }
    const submit = modal.querySelector("[data-inv-submit]")
    if (submit) { submit.textContent = "Sign & send for approval"; submit.removeAttribute("name"); submit.classList.remove("hidden") }
    const draftBtn = modal.querySelector("[data-inv-draft]")
    if (draftBtn) { draftBtn.textContent = "Save as draft"; draftBtn.classList.remove("hidden") }
    this._invDocsGate = true
    const title = modal.querySelector("[data-inv-title]")
    if (title) title.textContent = "New invoice"
    modal.querySelector("[data-ai-zone]")?.classList.remove("hidden")
    modal.querySelector("[data-inv-attach]")?.classList.remove("hidden")
    const label = modal.querySelector("[data-file-label]")
    if (label) label.textContent = "No file yet"
    const cache = form.querySelector("[data-cache-input]")
    if (cache) cache.value = ""
    const fname = form.querySelector("[data-filename-input]")
    if (fname) fname.value = ""
    const hist = modal.querySelector("[data-inv-history]")
    if (hist) { hist.innerHTML = ""; hist.classList.add("hidden") }
    const signBtn = modal.querySelector("[data-inv-sign]")
    if (signBtn) signBtn.classList.add("hidden")
    if (this.hasInvResendBarTarget) this.invResendBarTarget.classList.add("hidden")
    this._setPdfLink(modal.querySelector("[data-inv-pdf]"), null)
    this._signCtx = null
  }

  // Rebuild the breakdown grid from line data — saved rows for an edit, one
  // blank row for a new invoice; read-only views get everything disabled.
  _fillInvGrid(lines, editable) {
    if (!this.hasInvLinesBodyTarget) return
    this.invLinesBodyTarget.querySelectorAll("[data-inv-line]").forEach((r) => r.remove())
    const rows = lines.length ? lines : [{}]
    rows.forEach((line, i) => {
      this.addInvLine()
      const row = this.invLinesBodyTarget.querySelectorAll("[data-inv-line]")[i]
      const set = (name, v) => { const el = row.querySelector(`[name="app_lines[][${name}]"]`); if (el) { el.value = v ?? ""; el.disabled = !editable } }
      set("item", line.item ?? String(i + 1))
      set("description", line.description)
      set("quantity", line.quantity)
      set("unit", line.unit)
      set("unit_price", line.unit_price != null ? this._fmtMoney(line.unit_price) : "")
      const cell = row.querySelector("[data-inv-line-total]")
      if (cell) cell.textContent = `$${this._fmtMoney((line.quantity || 0) * (line.unit_price || 0))}`
      const remove = row.querySelector("button")
      if (remove) remove.classList.toggle("hidden", !editable)
    })
    if (this.hasInvModalTarget) {
      this.invModalTarget.querySelector("[data-inv-add-line]")?.classList.toggle("hidden", !editable)
    }
    this._recalcInvTotal()
  }

  // ── As-needed invoice breakdown (Phelipe 2026-07-28): PCO-style lines —
  // qty × unit price per row, the invoice total is the sum. ──────────────
  addInvLine() {
    if (!this.hasInvLinesBodyTarget) return
    const count = this.invLinesBodyTarget.querySelectorAll("[data-inv-line]").length
    const tr = document.createElement("tr")
    tr.setAttribute("data-inv-line", "")
    const cell = (name, extra = "", cls = "") =>
      `<td class="py-1 pr-3"><input type="text" name="app_lines[][${name}]" ${extra} class="w-full border border-gray-300 rounded px-1.5 py-1 text-[13px] disabled:bg-gray-100 disabled:text-gray-500 ${cls}"></td>`
    tr.innerHTML = `
      ${cell("item", `value="${count + 1}"`)}
      ${cell("description")}
      ${cell("quantity", 'data-action="input->sub-portal#invLineInput"', "text-right")}
      ${cell("unit")}
      <td class="py-1 pr-3"><div class="relative"><span class="absolute left-1.5 top-1/2 -translate-y-1/2 text-[13px] text-gray-400 pointer-events-none">$</span><input type="text" name="app_lines[][unit_price]" data-action="input->sub-portal#invLineInput blur->sub-portal#moneyBlur" class="w-full border border-gray-300 rounded pl-4 pr-1.5 py-1 text-right text-[13px] disabled:bg-gray-100 disabled:text-gray-500"></div></td>
      <td class="py-1 pl-3 text-right tabular-nums" data-inv-line-total>$0.00</td>
      <td class="py-1 text-right"><button type="button" data-action="click->sub-portal#removeInvLine" class="text-gray-300 hover:text-red-500 cursor-pointer" tabindex="-1">✕</button></td>`
    this.invLinesBodyTarget.appendChild(tr)
  }

  removeInvLine(e) {
    e.currentTarget.closest("[data-inv-line]")?.remove()
    // Never leave the grid empty — the first line stays open (Phelipe
    // 2026-07-28).
    if (!this.invLinesBodyTarget.querySelector("[data-inv-line]")) this.addInvLine()
    this._recalcInvTotal()
  }

  invLineInput(e) {
    const row = e.currentTarget.closest("[data-inv-line]")
    if (row) {
      const qty = this._num(row.querySelector('[name="app_lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="app_lines[][unit_price]"]')?.value)
      const cell = row.querySelector("[data-inv-line-total]")
      if (cell) cell.textContent = `$${this._fmtMoney(qty * price)}`
    }
    this._recalcInvTotal()
  }

  _recalcInvTotal() {
    if (!this.hasInvLinesTotalTarget || !this.hasInvLinesBodyTarget) return
    let total = 0
    this.invLinesBodyTarget.querySelectorAll("[data-inv-line]").forEach((row) => {
      const qty = this._num(row.querySelector('[name="app_lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="app_lines[][unit_price]"]')?.value)
      total += qty * price
    })
    this.invLinesTotalTarget.textContent = `$${this._fmtMoney(total)}`
  }

  lineInput(e) {
    const row = e.currentTarget.closest("[data-fee-line]")
    if (row && !e.currentTarget.hasAttribute("data-line-value")) {
      // qty × unit price previews into the Value box unless the sub typed one.
      const qty = this._num(row.querySelector('[name="lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="lines[][unit_price]"]')?.value)
      const valueEl = row.querySelector("[data-line-value]")
      if (valueEl && qty && price) valueEl.placeholder = this._fmtMoney(qty * price)
    }
    this._recalcTotal()
  }

  _recalcTotal() {
    if (!this.hasLinesTotalTarget || !this.hasLinesBodyTarget) return
    let total = 0
    this.linesBodyTarget.querySelectorAll("[data-fee-line]").forEach((row) => {
      const typed = this._num(row.querySelector("[data-line-value]")?.value)
      if (typed) { total += typed; return }
      const qty = this._num(row.querySelector('[name="lines[][quantity]"]')?.value)
      const price = this._num(row.querySelector('[name="lines[][unit_price]"]')?.value)
      total += qty * price
    })
    this.linesTotalTarget.textContent = "$" + total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // ── Fee schedule autosave (Phelipe 2026-07-24): no Save button — any
  // input/change in the form queues a save a second after typing stops. ──
  feeChanged() { this._queueFeeSave() }

  _queueFeeSave() {
    if (!this.hasFeeFormTarget) return
    clearTimeout(this._feeSaveTimer)
    this._feeSaveTimer = setTimeout(() => this._saveFeeSchedule(), 1000)
  }

  async _saveFeeSchedule() {
    const form = this.feeFormTarget
    const mode = form.querySelector('[name="baseline_mode"]:checked')?.value
    // Nothing worth saving yet — a fresh grid of blank rows or an empty
    // lump amount stays quiet instead of erroring on every keystroke.
    if (mode === "schedule") {
      const hasContent = [...this.linesBodyTarget.querySelectorAll("[data-fee-line]")].some((row) => {
        const g = (n) => row.querySelector(`[name="lines[][${n}]"]`)?.value?.trim() || ""
        return g("description") !== "" || this._num(g("scheduled_value")) || this._num(g("unit_price"))
      })
      if (!hasContent && !this._feeDeleted) return
    } else if (!this._num(form.querySelector('[name="amount"]')?.value)) {
      return
    }
    this._feeDeleted = false

    const status = this.hasFeeSaveStatusTarget ? this.feeSaveStatusTarget : null
    if (status) status.textContent = "Saving…"
    try {
      const resp = await fetch(form.action, {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: new FormData(form)
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      if (status) {
        status.textContent = data.had_approvals
          ? `Saved · $${data.total} — the approval request was reset, request it again when you're done`
          : `Saved · $${data.total}`
      }
    } catch (err) {
      if (status) status.textContent = err.message
    }
  }

  // Thousand separators on money inputs (Phelipe 2026-07-24): format on
  // blur and on AI fill; _num strips them back out for math and submit.
  _fmtMoney(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  moneyBlur(e) {
    const v = this._num(e.currentTarget.value)
    e.currentTarget.value = e.currentTarget.value.trim() === "" ? "" : this._fmtMoney(v)
  }

  _num(value) {
    const n = parseFloat(String(value || "").replace(/[$,]/g, ""))
    return Number.isFinite(n) ? n : 0
  }
}
